import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Play } from 'lucide-react';
import { useHaptics } from '@/hooks/useHaptics';
import { useSecondTick } from '@/lib/secondTick';

const STORAGE_KEY = 'active_workout';
const MIN_KEY = 'active_workout_minimised';

interface StoredWorkout {
  id: string;
  startedAt: string;
  exercises: Array<{ sets?: Array<{ completed?: boolean }> }>;
  notes?: string;
  hyroxName?: string;
}

function readName(notes: string | undefined, hyroxName: string | undefined): string {
  if (hyroxName) return hyroxName;
  if (notes) {
    try {
      const parsed = JSON.parse(notes);
      if (parsed?.customName && typeof parsed.customName === 'string') return parsed.customName;
    } catch { /* noop */ }
  }
  return 'Workout';
}

/**
 * Global floating pill shown above the bottom nav whenever a workout is
 * minimised. Persists across every screen; tapping returns to the full
 * active workout. Disappears automatically when the workout ends.
 */
export function ActiveWorkoutPill() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lightTap } = useHaptics();
  const [workout, setWorkout] = useState<StoredWorkout | null>(null);
  const [minimised, setMinimised] = useState(false);

  // Shared second-tick — single rAF ticker across the app.
  useSecondTick();

  useEffect(() => {
    const sync = () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        setWorkout(raw ? (JSON.parse(raw) as StoredWorkout) : null);
        setMinimised(!!localStorage.getItem(MIN_KEY));
      } catch {
        setWorkout(null);
        setMinimised(false);
      }
    };
    sync();
    const onVis = () => { if (!document.hidden) sync(); };
    window.addEventListener('storage', sync);
    window.addEventListener('active-workout-changed', sync);
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', onVis);
    const id = setInterval(sync, 5000);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('active-workout-changed', sync);
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(id);
    };
  }, []);

  // Hide on the active workout page itself, or if no workout, or not minimised.
  if (!workout || !minimised) return null;
  if (location.pathname === '/workout/active') return null;
  if (location.pathname === '/coach') return null;

  const name = readName(workout.notes, workout.hyroxName);
  const exercises = workout.exercises ?? [];
  const totalExercises = exercises.length;
  const completedExercises = exercises.filter(
    (ex) => (ex.sets?.length ?? 0) > 0 && (ex.sets ?? []).every((s) => s?.completed),
  ).length;

  const start = new Date(workout.startedAt).getTime();
  const elapsed = Math.max(0, Math.floor((Date.now() - start) / 1000));
  const h = Math.floor(elapsed / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const s = elapsed % 60;
  const timeLabel =
    h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      : `${m}:${String(s).padStart(2, '0')}`;

  const handleResume = () => {
    lightTap();
    try {
      localStorage.removeItem(MIN_KEY);
      window.dispatchEvent(new Event('active-workout-changed'));
    } catch { /* noop */ }
    navigate('/workout/active');
  };

  return (
    <div
      className="fixed left-0 right-0 z-[60] flex items-center justify-center px-4 pointer-events-none"
      style={{
        // Sit just above the floating pill bottom nav (nav is ~12px + 56px tall).
        bottom: 'calc(env(safe-area-inset-bottom) + 88px)',
      }}
    >
      <button
        onClick={handleResume}
        aria-label="Resume workout"
        className="pointer-events-auto flex items-center gap-3 pl-4 pr-1.5 py-1.5 active:scale-[0.98] transition-transform duration-150 bg-white/[0.92] dark:bg-[rgba(20,23,26,0.82)]"
        style={{
          maxWidth: 'calc(100% - 32px)',
          borderRadius: 20,
          border: '1px solid var(--hos-green-border)',
          backdropFilter: 'blur(22px) saturate(1.4)',
          WebkitBackdropFilter: 'blur(22px) saturate(1.4)',
          boxShadow: '0 10px 28px rgba(0,0,0,0.5)',
          color: 'var(--hos-text)',
        }}
      >
        {/* Live dot */}
        <span
          aria-hidden
          className="shrink-0 rounded-full pill-live-dot"
          style={{
            width: 9,
            height: 9,
            background: 'var(--hos-green)',
            boxShadow: '0 0 10px var(--hos-green-glow), 0 0 4px var(--hos-green)',
          }}
        />

        {/* Text block */}
        <span className="flex flex-col items-start min-w-0 leading-tight text-left">
          <span
            className="truncate max-w-[180px]"
            style={{ fontSize: '14.5px', fontWeight: 800, color: 'var(--hos-text)' }}
          >
            {name}
          </span>
          <span
            className="truncate max-w-[220px]"
            style={{ fontSize: '11.5px', color: 'var(--hos-text-2)', fontWeight: 500 }}
          >
            {completedExercises} of {totalExercises} exercise{totalExercises === 1 ? '' : 's'}
            {' · '}
            <span
              className="tabular-nums"
              style={{ color: 'var(--hos-text)', fontWeight: 700 }}
            >
              {timeLabel}
            </span>
          </span>
        </span>

        {/* Resume button */}
        <span
          className="shrink-0 flex items-center gap-1 pl-2.5 pr-3 py-2 ml-1"
          style={{
            background: 'var(--hos-green)',
            color: 'var(--hos-green-on-fill)',
            fontSize: 13,
            fontWeight: 800,
            borderRadius: 12,
          }}
        >
          <Play className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
          Resume
        </span>
      </button>
    </div>
  );
}
